import React, {useCallback,useEffect,useState} from 'react';
import {Alert,Button,Modal,Table} from 'react-bootstrap';
import {v4 as uuid} from 'uuid';
import {getCapabilities,getActionHistory,undoAction} from '../../../../services/ReconciliationActionService';
import {ACTION_TITLES} from './ActionPreview';

const linkStyle={fontSize:'0.8125rem',textDecoration:'none'};

export const isUndone=item=>!!item.undo_action_id||item.result.type==='undo_reconciliation';
export const canUndo=item=>!item.undo_action_id&&!!item.result.linked_bank;
export const bankLineHref=(companyId,item)=>item.plan?.bank?.id?`/${companyId}/bankstatements?filter=all&line=${item.plan.bank.id}`:null;

// An undo says which kind it was: the link was removed (the books untouched),
// or the booking was reversed.
const undoTitle=(item,unlink,reverse)=>(item.plan?.mode==='unlink'?unlink:reverse);
export function describe(item) {
    if(item.result.type==='undo_reconciliation')return undoTitle(item,'Bankkoppling borttagen','Bokföring återförd');
    const what=ACTION_TITLES[item.plan?.type]||'Avstämning';
    return item.undo_action_id?`${what} (ångrad)`:what;
}

// On an invoice page the row sits among the invoice's own events, so it says
// what happened (past tense) rather than naming the action.
const INVOICE_TITLES={register_customer_payment:'Betalning registrerad från bank',register_supplier_payment:'Betalning registrerad från bank',link_existing_payment:'Betalning kopplad till bank',link_existing_verification:'Verifikat kopplat till bank'};
export const describeOnInvoice=item=>item.result.type==='undo_reconciliation'?undoTitle(item,'Bankkoppling borttagen','Bokföring återförd'):INVOICE_TITLES[item.plan?.type]||'Avstämd mot bank';

/**
 * What has already been done with a bank line (or invoice), and the undo
 * flow. A host page renders `items` in its own idiom, calls `prepare(item,
 * 'unlink'|'reverse')` from its own text actions and mounts `modal` once.
 * `error` is set only while the modal is closed (the modal shows its own).
 */
export function useReconciliationHistory({companyId,statementId,invoiceId,onChanged}) {
    const [items,setItems]=useState([]),[plan,setPlan]=useState(null),[error,setError]=useState(''),[busy,setBusy]=useState(false);
    const refresh=useCallback(async()=>{const r=await getActionHistory(companyId,statementId?{statement_id:statementId}:{invoice_id:invoiceId});setItems(r.items||[]);},[companyId,statementId,invoiceId]);
    useEffect(()=>{getCapabilities(companyId).then(c=>c.human_undo?refresh():undefined).catch(()=>setError('Avstämningshistoriken kunde inte hämtas.'));},[companyId,refresh]);
    const prepare=async(item,mode)=>{
        setBusy(true);setError('');
        try{setPlan(await undoAction(companyId,item.action_id,{request_id:uuid(),mode}));}
        catch(e){setError(e.response?.data?.message||'Återföringen kunde inte förberedas.');}
        finally{setBusy(false);}
    };
    const execute=async()=>{
        setBusy(true);setError('');
        try{await undoAction(companyId,plan.original_action_id,{request_id:uuid(),undo_action_id:plan.action_id,plan_hash:plan.plan_hash});setPlan(null);await refresh();onChanged?.();}
        catch(e){setError(e.response?.data?.message||'Resultatet är okänt. Försök samma återföring igen för att kontrollera resultatet.');if(e.response?.status===409){setPlan(null);await refresh();onChanged?.();}}
        finally{setBusy(false);}
    };
    const modal=<Modal show={!!plan} onHide={()=>{if(!busy)setPlan(null);}}>
        <Modal.Header closeButton={!busy}><Modal.Title as="h6">{plan?.mode==='reverse'?'Återför bokföringen':'Ta bort bankkoppling'}</Modal.Title></Modal.Header>
        <Modal.Body style={{fontSize:'0.875rem'}}>
            <p>{plan?.mode==='reverse'?'Den valda betalningen eller bokningen återförs.':'Den befintliga betalningen och bokföringen behålls.'} Bankhändelsen öppnas för manuell hantering. Underlagets bokföringsägare behålls.</p>
            {plan?.snapshot?.verification?.date&&<p>Bokföringsdatum: {String(plan.snapshot.verification.date).slice(0,10)}</p>}
            {!!plan?.effects.journal_rows.length&&<Table size="sm"><thead><tr><th>Konto</th><th className="text-end">Debet</th><th className="text-end">Kredit</th></tr></thead><tbody>{plan.effects.journal_rows.map((r,i)=><tr key={i}><td>{r.account_number||r.account_id}</td><td className="text-end">{(r.debit_ore/100).toFixed(2)}</td><td className="text-end">{(r.credit_ore/100).toFixed(2)}</td></tr>)}</tbody></Table>}
            {error&&<Alert variant="warning" className="mb-0">{error}</Alert>}
        </Modal.Body>
        <Modal.Footer><Button variant="outline-secondary" size="sm" disabled={busy} onClick={()=>setPlan(null)}>Avbryt</Button><Button size="sm" disabled={busy} onClick={execute}>Bekräfta återföring</Button></Modal.Footer>
    </Modal>;
    return {items,error:plan?'':error,busy,prepare,modal};
}

/**
 * The bank-sheet form of the history: plain lines, no card, the undo actions
 * as text links in the sheet's own idiom. Renders nothing when there is
 * nothing to tell. The invoice pages render the hook's rows themselves.
 */
export default function ReconciliationHistory({companyId,statementId,invoiceId,onChanged}) {
    const {items,error,busy,prepare,modal}=useReconciliationHistory({companyId,statementId,invoiceId,onChanged});
    if(!items.length && !error)return null;
    return <section aria-label="Avstämningshistorik" style={{marginTop:22}}>
        {error&&<Alert variant="warning" className="py-2" style={{fontSize:'0.8125rem'}}>{error}</Alert>}
        {items.map(item=><div key={item.action_id} className="d-flex flex-wrap align-items-baseline" style={{fontSize:'0.8125rem',color:'#374151',columnGap:10,rowGap:2,marginBottom:6}}>
            <span style={isUndone(item)?{color:'#9ca3af'}:undefined}>
                {describe(item)}{item.result.committed_at?` · ${item.result.committed_at.slice(0,10)}`:''}
            </span>
            {!statementId&&bankLineHref(companyId,item)&&<a href={bankLineHref(companyId,item)} style={linkStyle}>Visa bankhändelse</a>}
            {canUndo(item)&&<>
                <Button variant="link" size="sm" className="p-0 text-muted" style={linkStyle} disabled={busy} onClick={()=>prepare(item,'unlink')}>Ta bort bankkoppling</Button>
                {item.result.created_verification&&<Button variant="link" size="sm" className="p-0 text-muted" style={linkStyle} disabled={busy} onClick={()=>prepare(item,'reverse')}>Återför bokföringen</Button>}
            </>}
        </div>)}
        {modal}
    </section>;
}
